import { inject, Injectable } from '@angular/core';
import { HEADERS } from '../api-headers';
import { HttpClient } from '@angular/common/http';

import {
  WIDGET_GET_CHART_TYPES,
  WIDGET_GET_GET_DEFAULT_STRUCTURE,
  WIDGET_GET_GET_OBJECT_WIDGETS,
  WIDGET_GET_WIDGET_BY_ID,
  WIDGET_POST_WIDGET_DATA,
  WIDGET_GET_DEFAULT_OUTPUT_FORMATS_SETTINGS,
  WIDGET_POST_EXPORT,
  WIDGET_POST_PREVIEW,
  WIDGET_PATCH_UPDATE,
  WIDGET_POST_SWAP_TYPE,
  WIDGET_POST_MASS_UPDATE,
  WIDGET_POST_MASS_OPERATION,
  WIDGET_DELETE_DELETE,
  WIDGET_DELETE_MASS_DELETE,
  WIDGET_POST_CREATE,
  WIDGET_PATCH_UPDATE_WIDGETS_ORDER,
} from '../endpoints/widget.endpoints';

import { ContentFilters, Widget } from 'src/app/core/models/core.models';
import { NomLanguages } from 'src/app/core/constants';
import { OutputSettings } from 'src/app/core/models/output-settings.models';
import { WidgetOperation } from 'src/app/core/models/widget-operations';
import { WidgetVizType } from 'src/app/core/models/widget.models';

import { toQueryParams } from '../api-utils';
import {
  CreateWidgetRequest,
  WidgetExportFormat,
  createWidget,
  widgetData,
  widgetsOrder,
} from '../serialize/widget.serialize';

import { map, Observable } from 'rxjs';

/**
 * `/widget/mass_update` answers 200 with a status field rather than an HTTP
 * error, so the failure has to be read out of the body — see `massUpdate`.
 */
export interface MassUpdateResponse {
  status: 'success' | 'error';
  updated_count?: number;
  message?: string;
  widgets?: Widget[];
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class WidgetApiService {
  private readonly http = inject(HttpClient);

  getWidget(widgetId: string): Observable<Widget> {
    const url = WIDGET_GET_WIDGET_BY_ID(widgetId);
    return this.http.get<Widget>(url).pipe(map((response: any) => response));
  }

  getWidgetOutputFormatsSettings(widgetId: string): Observable<OutputSettings> {
    const params = toQueryParams({ widget_id: widgetId });

    return this.http.get<OutputSettings>(
      WIDGET_GET_DEFAULT_OUTPUT_FORMATS_SETTINGS,
      {
        params,
      },
    );
  }

  getVizTypes(chartId: number): Observable<Array<WidgetVizType>> {
    const params = toQueryParams({ chart_id: chartId });

    return this.http.get<Array<WidgetVizType>>(WIDGET_GET_CHART_TYPES, {
      params,
    });
  }

  /**
   * Widgets of a feed or dashboard. **`pageId` omitted means every page** — that
   * is the Presentation Mode read, and it is deliberately a different request
   * (and a different query key) from a single dashboard page's. Read WAP-874
   * before merging the two.
   */
  getObjectWidgets(
    objectId: string,
    objectClassId: number,
    pageId?: number,
  ): Observable<Widget[]> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
      page_id: pageId ?? null,
    });

    return this.http.get<Widget[]>(WIDGET_GET_GET_OBJECT_WIDGETS, { params });
  }

  getWidgetPreview(
    widget: any,
    contentFilters: ContentFilters,
    languageId: number = NomLanguages.ENGLISH,
    cacheEnabled: boolean = true,
  ): Observable<any> {
    const params = {
      widget: widget,
      content_filters: contentFilters,
      lid: languageId,
      cache_enabled: cacheEnabled,
    };
    return this.http.post(WIDGET_POST_PREVIEW, params);
  }

  getDefaultWidgetStructure(
    objectId: string,
    objectClassId: number,
    chartId: number,
    chartTypeId: number,
    estimateNextRow: boolean = false,
  ): Observable<Widget> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
      chart_id: chartId,
      chart_type_id: chartTypeId,
      estimate_next_row: estimateNextRow,
    });

    return this.http.get<Widget>(WIDGET_GET_GET_DEFAULT_STRUCTURE, { params });
  }

  /** Triggers a widget export with a specific format. */
  exportWidget(
    widgetId: string,
    contentFilters: ContentFilters,
    languageId: number,
    exportFormat: WidgetExportFormat,
    cacheEnabled: boolean = false,
  ): Observable<Blob> {
    return this.http.post(
      WIDGET_POST_EXPORT(widgetId),
      widgetData.toMultipartFormData(
        contentFilters,
        languageId,
        cacheEnabled,
        exportFormat,
      ),
      { responseType: 'blob' },
    );
  }

  /**
   * Create a widget. The multipart body (and its two legacy quirks) lives in
   * `api/serialize/widget.serialize.ts`.
   */
  createWidget(request: CreateWidgetRequest): Observable<Widget> {
    return this.http
      .post<Widget>(
        WIDGET_POST_CREATE,
        createWidget.toMultipartFormData(request),
      )
      .pipe(map((response: any) => response));
  }

  /**
   * Persist layout (`col` / `row` / `sizeX` / `sizeY`) for a whole view's
   * widgets. The **whole list** goes in every time — that is what the endpoint
   * expects, and it is why WAP-874's corrupted coordinates were persisted for
   * every widget at once.
   */
  updateWidgetsOrder(widgets: Widget[]): Observable<unknown> {
    return this.http.patch(
      WIDGET_PATCH_UPDATE_WIDGETS_ORDER,
      widgetsOrder.toUrlEncoded(widgets),
      { headers: HEADERS.formUrlEncodedNoCharset },
    );
  }

  /** PATCH the whole widget object. Callers build the payload. */
  updateWidget(widgetId: string, widget: any): Observable<Widget> {
    return this.http
      .patch<Widget>(WIDGET_PATCH_UPDATE(widgetId), widget)
      .pipe(map((response: any) => response));
  }

  /**
   * Swap a widget's preset + visualization. The backend mutates and saves the
   * *same* widget (verified in `WidgetPOST::swapType`), so the returned widget
   * carries the same id.
   */
  swapWidgetType(
    widgetId: string,
    chartId: number,
    chartTypeId: number,
  ): Observable<Widget> {
    const formData = new FormData();
    formData.append('chart_id', chartId.toString());
    formData.append('chart_type_id', chartTypeId.toString());

    return this.http
      .post<Widget>(WIDGET_POST_SWAP_TYPE(widgetId), formData)
      .pipe(map((response: any) => response));
  }

  /**
   * Apply one settings payload to many widgets. The endpoint answers 200 with
   * `status: 'error'` on a business failure, so that is turned into a thrown
   * error here — the mutation's `onError` needs it to toast.
   */
  massUpdate(
    widgetIds: string[],
    updateData: unknown,
  ): Observable<MassUpdateResponse> {
    return this.http
      .post<MassUpdateResponse>(WIDGET_POST_MASS_UPDATE, {
        widget_ids: widgetIds,
        update_data: updateData,
      })
      .pipe(
        map((response) => {
          if (response.status === 'error') {
            throw new Error(response.message || 'Update failed');
          }

          return response;
        }),
      );
  }

  /** Copy/move widgets between views. The operations array is posted raw. */
  massOperation(operations: WidgetOperation[]): Observable<boolean> {
    return this.http.post<boolean>(WIDGET_POST_MASS_OPERATION, operations);
  }

  deleteWidget(widgetId: string): Observable<unknown> {
    return this.http.delete(WIDGET_DELETE_DELETE(widgetId));
  }

  /**
   * Bulk delete. The ids go **in the body** as urlencoded repeated keys
   * (`widgetIds[]`), which is what the backend route reads.
   */
  deleteWidgets(widgetIds: string[]): Observable<unknown> {
    const body = new URLSearchParams();
    widgetIds.forEach((widgetId) => body.append('widgetIds[]', widgetId));

    return this.http.delete(WIDGET_DELETE_MASS_DELETE, { body });
  }

  /**
   * Data for a single widget. The multipart body is shared with the export
   * request, so it lives in `api/serialize/widget.serialize.ts`.
   */
  getWidgetData(
    widgetId: string,
    contentFilters: ContentFilters,
    languageId: number,
    cacheEnabled: boolean,
  ): Observable<any> {
    return this.http.post(
      WIDGET_POST_WIDGET_DATA(widgetId),
      widgetData.toMultipartFormData(contentFilters, languageId, cacheEnabled),
    );
  }
}
